import { Label } from '@slal/ui/forms';
import { Article, Heading, P } from '@slal/ui/typography';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { DisplayDesktop, DisplayMobile, MaxWidthContent, Notification, Pagination } from '~/components';
import { useFundSelector, useListFunds } from '~/hooks';
import { FundAllocationForm } from '../application';
import { FundChargesModal } from './FundChargesModal';
import { FundList, FundListLoading, FundTable, FundTableLoading } from './FundList';
import {
    FundChoicesLink,
    FundSearch,
    FundSearchContainer,
    FundSearchIcon,
    NoSearchResultsNotice,
    StyledFundOptions,
    StyledFundOptionsFooter,
} from './styles';
import { VolatilityExplainedModal, VolatilityRatings } from './VolatilityRatings';

export const FundOptions = () => {
    const fundSelectorResponse = useFundSelector();
    const { error, loading, funds, pagination, setSearchTerm } = useListFunds();
    const [isNoticeVisible, setIsNoticeVisible] = useState(false);

    const [isVolatilityExplainedModalopen, setIsVolatilityExplainedModalOpen] = useState(false);
    const handleCloseVolatilityExplainedModal = useCallback(
        () => setIsVolatilityExplainedModalOpen(false),
        [setIsVolatilityExplainedModalOpen]
    );
    const handleOpenVolatilityExplainedModal = useCallback(
        () => setIsVolatilityExplainedModalOpen(true),
        [setIsVolatilityExplainedModalOpen]
    );

    const [isFundChargesModalOpen, setIsFundChargesModalOpen] = useState(false);
    const handleCloseFundChargesModal = useCallback(
        () => setIsFundChargesModalOpen(false),
        [setIsFundChargesModalOpen]
    );

    const handleOpenFundChargesModal = useCallback(() => setIsFundChargesModalOpen(true), [setIsFundChargesModalOpen]);

    const handleSearch = useCallback(
        debounce(event => setSearchTerm(event.target.value), 400),
        [setSearchTerm]
    );

    useEffect(() => {
        setIsNoticeVisible(!funds?.length && !loading);
    }, [funds, loading]);

    return (
        <>
            <MaxWidthContent>
                <Article>
                    <Heading level={3}>Full fund range</Heading>
                    <P>
                        You can invest in up to 12 funds. If you change your mind later, you can switch funds free of
                        charge.
                    </P>
                    <FundChoicesLink href="https://lib.standardlife.com/library/uk/ampp5a.pdf">
                        Fund choices
                    </FundChoicesLink>
                    <FundSearchContainer>
                        <FundSearch id="search-fund" placeholder="Search our full fund range" onChange={handleSearch} />
                        <Label htmlFor="search-fund">
                            <FundSearchIcon />
                        </Label>
                    </FundSearchContainer>
                </Article>

                <VolatilityRatings handleOpenVolatilityExplainedModal={handleOpenVolatilityExplainedModal} />
                <VolatilityExplainedModal
                    isOpen={isVolatilityExplainedModalopen}
                    onClose={handleCloseVolatilityExplainedModal}
                />
                <FundChargesModal isOpen={isFundChargesModalOpen} onClose={handleCloseFundChargesModal} />

                {error && (
                    <Notification heading="Something went wrong">
                        There has been a problem loading the fund options. Please try again later.
                    </Notification>
                )}
            </MaxWidthContent>

            {!error && (
                <StyledFundOptions>
                    <DisplayMobile>
                        {loading ? <FundListLoading /> : <FundList funds={funds} {...fundSelectorResponse} />}
                    </DisplayMobile>
                    <DisplayDesktop>
                        {loading ? (
                            <FundTableLoading
                                handleOpenFundChargesModal={handleOpenFundChargesModal}
                                handleOpenVolatilityExplainedModal={handleOpenVolatilityExplainedModal}
                            />
                        ) : (
                            <FundTable
                                funds={funds}
                                handleOpenVolatilityExplainedModal={handleOpenVolatilityExplainedModal}
                                handleOpenFundChargesModal={handleOpenFundChargesModal}
                                {...fundSelectorResponse}
                            />
                        )}
                    </DisplayDesktop>
                    <StyledFundOptionsFooter isNoticeVisible={isNoticeVisible}>
                        {isNoticeVisible && <NoSearchResultsNotice>No funds match your search.</NoSearchResultsNotice>}

                        <Pagination aria-label="Fund pagination" {...pagination} />
                    </StyledFundOptionsFooter>
                    <FundAllocationForm {...fundSelectorResponse} />
                </StyledFundOptions>
            )}
        </>
    );
};
